import React, { useEffect, useRef, RefObject } from "react";

const createHotkey = (key: string, action: () => void, ref: RefObject<HTMLFormElement | null>) => {
  const listener = (e: KeyboardEvent) => {
    // Metakey is the attribute that indicates the Cmd button is pressed
    if (e.metaKey && e.code == key) {
      e.stopPropagation(); // Prevent propogation to other listeners (in case there are multiple forms)
      e.preventDefault();
      action();
    }
  };

  useEffect(() => {
    // Attach a "keydown" event listener to the HTML element passed via Ref
    ref.current?.addEventListener("keydown", listener);
    return () => {
      ref.current?.removeEventListener("keydown", listener);
    };
  }, [action]);
};

type FormProps = {
  className: string;
  onSubmit?: (e: React.ChangeEvent<HTMLFormElement>) => void;
  submitButtonRef: RefObject<HTMLButtonElement | null>;
  children: React.ReactNode;
};
const Form = ({ className, onSubmit, submitButtonRef, children }: FormProps) => {
  // Create hotkey that simulates a "Submit" button click on Cmd+Enter
  const simulateSubmitButtonClick = () => submitButtonRef.current?.click();

  const onSubmitPreventDefault = (e: React.ChangeEvent<HTMLFormElement>) => {
    // Provide the Form with a default "onSubmit" handler, so that the form doesn't use the default behavior
    // For example, if the Form component is used with no onSubmit provided, and we don't preventDefault, using the Enter key will try to
    // "submit" the form, when nothing actually results from that, resulting in unexpected behavior
    e.stopPropagation();
    e.preventDefault();
    // But, if there is a submit behavior provided, execute that behavior
    if (onSubmit) {
      onSubmit(e);
    }
  };

  // FormRef must be passed to set the context for the hotkey
  // If there are multiple forms on the page, the FormRef will stop the keypress event from propegating to other forms
  const formRef = useRef<HTMLFormElement>(null);
  createHotkey("Enter", simulateSubmitButtonClick, formRef);

  return (
    <form ref={formRef} onSubmit={onSubmitPreventDefault} className={className}>
      {children}
    </form>
  );
};

export default Form;
